import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { StudyTimepoint } from '../../types/study.types';

interface SurveyScheduleBuilderProps {
  timepoints: StudyTimepoint[];
  onChange: (timepoints: StudyTimepoint[]) => void;
  availableSurveys: { id: string; title: string }[];
}

const SurveyScheduleBuilder: React.FC<SurveyScheduleBuilderProps> = ({
  timepoints,
  onChange,
  availableSurveys,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const addTimepoint = () => {
    const newTimepoint: StudyTimepoint = {
      id: uuidv4(),
      name: `Week ${timepoints.length * 4}`,
      daysFromEnrollment: timepoints.length * 28,
      surveyId: '',
      windowDaysBefore: 3,
      windowDaysAfter: 7,
      isBaseline: timepoints.length === 0,
    };
    onChange([...timepoints, newTimepoint]);
    setEditingIndex(timepoints.length);
  };

  const updateTimepoint = (index: number, updates: Partial<StudyTimepoint>) => {
    const updated = [...timepoints];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeTimepoint = (index: number) => {
    if (!window.confirm('Remove this timepoint?')) return;
    const updated = timepoints.filter((_, i) => i !== index);
    onChange(updated);
    if (editingIndex === index) setEditingIndex(null);
  };

  const moveTimepoint = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= timepoints.length) return;

    const updated = [...timepoints];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
    setEditingIndex(newIndex);
  };

  // Sort timepoints by days from enrollment
  const sortedTimepoints = [...timepoints].sort((a, b) => a.daysFromEnrollment - b.daysFromEnrollment);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Survey Schedule</h3>
        <button
          type="button"
          onClick={addTimepoint}
          className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          + Add Timepoint
        </button>
      </div>

      {timepoints.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No timepoints added yet</p>
          <p className="text-xs text-gray-400">Click "Add Timepoint" to create your survey schedule</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Timeline visualization */}
          <div className="relative bg-gray-50 rounded-lg p-4 overflow-x-auto">
            <div className="flex items-center min-w-max">
              <div className="flex items-center">
                <div className="h-3 w-3 bg-teal-600 rounded-full"></div>
                <span className="ml-2 text-xs text-gray-600">Enrollment</span>
              </div>
              {sortedTimepoints.map((tp, idx) => (
                <React.Fragment key={tp.id}>
                  <div
                    className="h-0.5 bg-gray-300"
                    style={{ width: `${Math.min(tp.daysFromEnrollment * 2, 100)}px` }}
                  ></div>
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-3 w-3 rounded-full ${
                        tp.isBaseline ? 'bg-blue-600' : 'bg-teal-600'
                      }`}
                    ></div>
                    <span className="text-xs text-gray-600 mt-1 whitespace-nowrap">{tp.name}</span>
                    <span className="text-xs text-gray-400">Day {tp.daysFromEnrollment}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Timepoint cards */}
          <div className="space-y-2">
            {timepoints.map((tp, index) => (
              <div
                key={tp.id}
                className={`bg-white border rounded-lg p-4 ${
                  editingIndex === index ? 'border-teal-500 ring-2 ring-teal-200' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{tp.name}</span>
                    {tp.isBaseline && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                        Baseline
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveTimepoint(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveTimepoint(index, 'down')}
                      disabled={index === timepoints.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTimepoint(index)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  Day {tp.daysFromEnrollment} • Window: -{tp.windowDaysBefore}/+{tp.windowDaysAfter} days •{' '}
                  {tp.surveyId
                    ? availableSurveys.find((s) => s.id === tp.surveyId)?.title || 'Survey selected'
                    : 'No survey assigned'}
                </div>

                {/* Expanded edit form */}
                {editingIndex === index && (
                  <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Timepoint Name
                      </label>
                      <input
                        type="text"
                        value={tp.name}
                        onChange={(e) => updateTimepoint(index, { name: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="e.g., Baseline, Week 4"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Days from Enrollment
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={tp.daysFromEnrollment}
                        onChange={(e) =>
                          updateTimepoint(index, { daysFromEnrollment: parseInt(e.target.value) || 0 })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Survey</label>
                      <select
                        value={tp.surveyId}
                        onChange={(e) => updateTimepoint(index, { surveyId: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="">Select a survey...</option>
                        {availableSurveys.map((survey) => (
                          <option key={survey.id} value={survey.id}>
                            {survey.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Window Before (days)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={tp.windowDaysBefore}
                          onChange={(e) =>
                            updateTimepoint(index, { windowDaysBefore: parseInt(e.target.value) || 0 })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Window After (days)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={tp.windowDaysAfter}
                          onChange={(e) =>
                            updateTimepoint(index, { windowDaysAfter: parseInt(e.target.value) || 0 })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tp.isBaseline}
                          onChange={(e) => updateTimepoint(index, { isBaseline: e.target.checked })}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">This is the baseline timepoint</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick add presets */}
      {timepoints.length === 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="text-xs text-gray-500">Quick add:</span>
          <button
            type="button"
            onClick={() => {
              const presets: StudyTimepoint[] = [
                {
                  id: uuidv4(),
                  name: 'Baseline',
                  daysFromEnrollment: 0,
                  surveyId: '',
                  windowDaysBefore: 0,
                  windowDaysAfter: 7,
                  isBaseline: true,
                },
                {
                  id: uuidv4(),
                  name: 'Week 4',
                  daysFromEnrollment: 28,
                  surveyId: '',
                  windowDaysBefore: 3,
                  windowDaysAfter: 7,
                  isBaseline: false,
                },
                {
                  id: uuidv4(),
                  name: 'Week 8',
                  daysFromEnrollment: 56,
                  surveyId: '',
                  windowDaysBefore: 3,
                  windowDaysAfter: 7,
                  isBaseline: false,
                },
                {
                  id: uuidv4(),
                  name: 'Week 12',
                  daysFromEnrollment: 84,
                  surveyId: '',
                  windowDaysBefore: 3,
                  windowDaysAfter: 7,
                  isBaseline: false,
                },
              ];
              onChange(presets);
            }}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            12-week study (4 timepoints)
          </button>
          <button
            type="button"
            onClick={() => {
              const presets: StudyTimepoint[] = [
                {
                  id: uuidv4(),
                  name: 'Baseline',
                  daysFromEnrollment: 0,
                  surveyId: '',
                  windowDaysBefore: 0,
                  windowDaysAfter: 7,
                  isBaseline: true,
                },
                {
                  id: uuidv4(),
                  name: 'Month 3',
                  daysFromEnrollment: 90,
                  surveyId: '',
                  windowDaysBefore: 7,
                  windowDaysAfter: 14,
                  isBaseline: false,
                },
                {
                  id: uuidv4(),
                  name: 'Month 6',
                  daysFromEnrollment: 180,
                  surveyId: '',
                  windowDaysBefore: 7,
                  windowDaysAfter: 14,
                  isBaseline: false,
                },
              ];
              onChange(presets);
            }}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            6-month study (3 timepoints)
          </button>
        </div>
      )}
    </div>
  );
};

export default SurveyScheduleBuilder;
